import { createContext, useContext, useCallback, useMemo } from "react";
import { useSearch, useLocation } from "wouter";

type ShelterSelection = "system" | string;

interface InsightsContextValue {
  selectedShelter: ShelterSelection;
  setSelectedShelter: (shelter: ShelterSelection) => void;
  isSystemView: boolean;
}

const InsightsContext = createContext<InsightsContextValue | null>(null);

export function useInsightsContext() {
  const context = useContext(InsightsContext);
  if (!context) {
    throw new Error("useInsightsContext must be used within InsightsProvider");
  }
  return context;
}

interface InsightsProviderProps {
  children: React.ReactNode;
}

export function InsightsProvider({ children }: InsightsProviderProps) {
  const searchString = useSearch();
  const [location, setLocation] = useLocation();
  
  const urlParams = new URLSearchParams(searchString);
  const shelterFromUrl = urlParams.get("shelter");
  
  const selectedShelter: ShelterSelection = shelterFromUrl || "system";
  const isSystemView = selectedShelter === "system";

  const setSelectedShelter = useCallback((shelter: ShelterSelection) => {
    const newParams = new URLSearchParams(searchString);
    if (shelter === "system") {
      newParams.delete("shelter");
    } else {
      newParams.set("shelter", shelter);
    }
    const newSearch = newParams.toString();
    const basePath = location.split("?")[0];
    setLocation(newSearch ? `${basePath}?${newSearch}` : basePath);
  }, [location, searchString, setLocation]);

  const value = useMemo(() => ({
    selectedShelter,
    setSelectedShelter,
    isSystemView,
  }), [selectedShelter, setSelectedShelter, isSystemView]);

  return (
    <InsightsContext.Provider value={value}>
      {children}
    </InsightsContext.Provider>
  );
}
